/**
 * Configuration déclarative pour l'entité Prospect (FormSubmission)
 * Source unique de vérité pour tous les champs de Prospect
 *
 * Note: Les prospects sont des FormSubmission avec des données imbriquées
 * (form.origin.label, contact.first_name, etc.).
 * La config utilise donc csvTransform custom car la structure est complexe.
 */

import type { FormSubmission } from '../../types/form';
import { defineEntityConfig } from '../../lib/entity-config';

export const PROSPECT_CONFIG = defineEntityConfig<FormSubmission>({
  entityName: 'prospect',
  entityNamePlural: 'prospects',

  fields: {
    // Formulaire (form_id) - Nécessaire pour l'import
    form_id: {
      key: 'form_id',
      label: 'Formulaire (ID)',
      type: 'text',
      sortable: false,
      showInTable: false,
      hiddenInColumnSelector: true, // Champ technique
      showInFilters: false,
      exportable: false,
      importable: true, // ✅ Nécessaire pour créer un prospect
      required: true,
    },

    // Origine (via form.origin.label)
    origin: {
      key: 'form',
      label: 'Origine',
      type: 'relation',
      sortable: false,
      showInTable: true,
      showInFilters: false,
      exportable: true,
      importable: false,
      exportTransform: (form: any) => form?.origin?.label || '-',
    },

    // Produit (via form.product.name)
    product: {
      key: 'form',
      label: 'Produit',
      type: 'relation',
      sortable: false,
      showInTable: true,
      showInFilters: false,
      exportable: true,
      importable: false,
      exportTransform: (form: any) => form?.product?.name || '-',
    },

    // Nom (via contact.last_name)
    last_name: {
      key: 'contact',
      label: 'Nom',
      type: 'relation',
      sortable: true,
      searchable: true,
      showInTable: true,
      showInFilters: true,
      exportable: true,
      importable: true, // ✅ Importable
      required: true, // ✅ Obligatoire pour l'import
      exportTransform: (contact: any) => contact?.last_name || '-',
    },

    // Prénom (via contact.first_name)
    first_name: {
      key: 'contact',
      label: 'Prénom',
      type: 'relation',
      sortable: true,
      searchable: true,
      showInTable: true,
      showInFilters: true,
      exportable: true,
      importable: true, // ✅ Importable
      required: true, // ✅ Obligatoire pour l'import
      exportTransform: (contact: any) => contact?.first_name || '-',
    },

    // Email (via contact.email)
    email: {
      key: 'contact',
      label: 'Email',
      type: 'relation',
      sortable: false,
      searchable: true,
      showInTable: true,
      showInFilters: true,
      exportable: true,
      importable: true, // ✅ Importable
      required: true, // ✅ Obligatoire pour l'import
      exportTransform: (contact: any) => contact?.email || '-',
    },

    // Téléphone (via contact.phone)
    phone: {
      key: 'contact',
      label: 'Téléphone',
      type: 'relation',
      sortable: false,
      searchable: true,
      showInTable: true,
      showInFilters: true,
      exportable: true,
      importable: true, // ✅ Importable (optionnel)
      required: false,
      exportTransform: (contact: any) => contact?.phone || '-',
    },

    // Assigné à (via contact.assigned_user.full_name)
    assigned_user: {
      key: 'contact',
      label: 'Assigné à',
      type: 'relation',
      sortable: false,
      showInTable: true,
      showInFilters: false,
      exportable: true,
      importable: false,
      exportTransform: (contact: any) => contact?.assigned_user?.full_name || '-',
    },

    // Date de soumission
    created_at: {
      key: 'created_at',
      label: 'Date de soumission',
      type: 'date',
      sortable: true,
      showInTable: true,
      showInFilters: false,
      exportable: true,
      importable: false,
      exportTransform: (value: string) =>
        value ? new Date(value).toLocaleDateString('fr-FR') : '-',
    },
  },
});
